type MachineEventObject = {
    type: string;
    [key: string]: unknown;
};

type MachineEvent = string | MachineEventObject;
type MachineStateValue = string | readonly string[];

type MachineSnapshot<TContext, TProps> = Readonly<{
    value: MachineStateValue;
    tags: readonly string[];
    context: Readonly<TContext>;
    props: Readonly<TProps>;
    generations: Readonly<Record<string, number>>;
}>;

type MachineEntry<TContext, TProps> = Readonly<{
    path: string;
    generation: number;
    from: MachineStateValue;
    to: MachineStateValue;
    event: MachineEventObject;
    snapshot: MachineSnapshot<TContext, TProps>;
}>;

type MachineScope<TContext, TProps> = {
    snapshot: MachineSnapshot<TContext, TProps>;
    context: Readonly<TContext>;
    props: Readonly<TProps>;
    event: MachineEventObject;
    state: MachineStateValue;
};

type MachineGuard<TContext = unknown, TProps = Record<string, never>> = (
    scope: MachineScope<TContext, TProps>,
) => boolean;

type MachineUpdate<TContext, TProps> = {
    context?: Partial<TContext>;
    props?: Partial<TProps>;
};

type MachineReducer<TContext = unknown, TProps = Record<string, never>> = (
    scope: MachineScope<TContext, TProps>,
) => MachineUpdate<TContext, TProps> | void;

type MachineEffect<TContext = unknown, TProps = Record<string, never>> = (
    scope: MachineScope<TContext, TProps>,
) => void | (() => void);

type MachineReducerRef<TContext, TProps> = string | MachineReducer<TContext, TProps>;
type MachineEffectRef<TContext, TProps> = string | MachineEffect<TContext, TProps>;
type MachineReducerList<TContext, TProps> = MachineReducerRef<TContext, TProps> | MachineReducerRef<TContext, TProps>[];
type MachineEffectList<TContext, TProps> = MachineEffectRef<TContext, TProps> | MachineEffectRef<TContext, TProps>[];

type MachineTransitionDefinition<TContext, TProps> = {
    target?: string;
    guard?: string | MachineGuard<TContext, TProps>;
    reduce?: MachineReducerList<TContext, TProps>;
    effects?: MachineEffectList<TContext, TProps>;
};

type MachineTransition<TContext, TProps = Record<string, never>> = string | MachineTransitionDefinition<TContext, TProps>;

type MachineStateConfig<TContext, TProps = Record<string, never>> = {
    id?: string;
    type?: 'compound' | 'parallel' | 'final';
    initial?: string;
    tags?: string[];
    reduce?: MachineReducerList<TContext, TProps>;
    entry?: MachineEffectList<TContext, TProps>;
    exit?: MachineEffectList<TContext, TProps>;
    on?: Record<string, MachineTransition<TContext, TProps> | Array<MachineTransition<TContext, TProps>>>;
    states?: Record<string, MachineStateConfig<TContext, TProps>>;
};

type MachineConfig<TContext, TProps = Record<string, never>> = MachineStateConfig<TContext, TProps> & {
    initial?: string;
    states: Record<string, MachineStateConfig<TContext, TProps>>;
};

type MachineOptions<TContext, TProps = Record<string, never>> = {
    context: TContext;
    props?: TProps;
    reducers?: Record<string, MachineReducer<TContext, TProps>>;
    guards?: Record<string, MachineGuard<TContext, TProps>>;
    effects?: Record<string, MachineEffect<TContext, TProps>>;
};

type Machine<TContext, TProps = Record<string, never>> = {
    readonly state: MachineStateValue;
    readonly context: Readonly<TContext>;
    readonly props: Readonly<TProps>;
    getSnapshot(): MachineSnapshot<TContext, TProps>;
    send(event: MachineEvent): void;
    subscribe(listener: (snapshot: MachineSnapshot<TContext, TProps>) => void): () => void;
    subscribeEntries(listener: (entry: MachineEntry<TContext, TProps>) => void): () => void;
    matches(state: string): boolean;
    hasTag(tag: string): boolean;
    stop(): void;
};

type RuntimeNode<TContext, TProps> = {
    key: string;
    path: string;
    order: number;
    id?: string;
    parent?: string;
    type: 'compound' | 'parallel' | 'final';
    initial?: string;
    children: string[];
    tags: string[];
    reduce: MachineReducerRef<TContext, TProps>[];
    entry: MachineEffectRef<TContext, TProps>[];
    exit: MachineEffectRef<TContext, TProps>[];
    on: Record<string, MachineTransition<TContext, TProps> | Array<MachineTransition<TContext, TProps>>>;
};

type SelectedTransition<TContext, TProps> = {
    source: RuntimeNode<TContext, TProps>;
    definition: MachineTransitionDefinition<TContext, TProps>;
};

const ROOT_PATH = '$';
const INIT_EVENT: MachineEventObject = {type: '@@init'};

const toEventObject = (event: MachineEvent): MachineEventObject => (
    typeof event === 'string' ? {type: event} : event
);

const toArray = <T>(value: T | T[] | undefined): T[] => {
    if (value === undefined) return [];
    return Array.isArray(value) ? value : [value];
};

const asDefinition = <TContext, TProps>(
    transition: MachineTransition<TContext, TProps>,
): MachineTransitionDefinition<TContext, TProps> => (
    typeof transition === 'string' ? {target: transition} : transition
);

const isDescendant = (path: string, ancestor: string): boolean => (
    ancestor === ROOT_PATH || path === ancestor || path.startsWith(`${ancestor}.`)
);

const createMachine = <TContext, TProps = Record<string, never>>(
    config: MachineConfig<TContext, TProps>,
    options: MachineOptions<TContext, TProps>,
): Machine<TContext, TProps> => {
    const nodes = new Map<string, RuntimeNode<TContext, TProps>>();
    const ids = new Map<string, string>();
    const listeners = new Set<(snapshot: MachineSnapshot<TContext, TProps>) => void>();
    const entryListeners = new Set<(entry: MachineEntry<TContext, TProps>) => void>();
    const generationCounters = new Map<string, number>();
    const activeGenerations = new Map<string, number>();
    const cleanups = new Map<string, Array<() => void>>();
    const queue: MachineEventObject[] = [];
    let processing = false;
    let stopped = false;
    let order = 0;

    const visit = (
        states: Record<string, MachineStateConfig<TContext, TProps>>,
        parent: string,
    ): string[] => Object.entries(states).map(([key, stateConfig]) => {
        const path = parent === ROOT_PATH ? key : `${parent}.${key}`;
        const children = stateConfig.states ? visit(stateConfig.states, path) : [];
        const type = stateConfig.type ?? (children.length > 0 ? 'compound' : 'final');
        const node: RuntimeNode<TContext, TProps> = {
            key,
            path,
            order: order++,
            type,
            children,
            tags: stateConfig.tags ?? [],
            reduce: toArray(stateConfig.reduce),
            entry: toArray(stateConfig.entry),
            exit: toArray(stateConfig.exit),
            on: stateConfig.on ?? {},
        };
        if (stateConfig.id) node.id = stateConfig.id;
        if (parent !== ROOT_PATH) node.parent = parent;
        if (stateConfig.initial) node.initial = stateConfig.initial;
        nodes.set(path, node);
        if (node.id) ids.set(node.id, path);
        return path;
    });

    const rootChildren = visit(config.states, ROOT_PATH);
    const root: RuntimeNode<TContext, TProps> = {
        key: ROOT_PATH,
        path: ROOT_PATH,
        order: -1,
        type: config.type ?? 'compound',
        children: rootChildren,
        tags: config.tags ?? [],
        reduce: toArray(config.reduce),
        entry: toArray(config.entry),
        exit: toArray(config.exit),
        on: config.on ?? {},
    };
    if (config.id) root.id = config.id;
    if (config.initial) root.initial = config.initial;
    nodes.set(ROOT_PATH, root);
    if (root.id) ids.set(root.id, ROOT_PATH);

    const nodeFor = (path: string): RuntimeNode<TContext, TProps> => {
        const node = nodes.get(path);
        if (!node) throw new Error(`Unknown machine state: ${path}`);
        return node;
    };

    const ancestry = (path: string): RuntimeNode<TContext, TProps>[] => {
        const result: RuntimeNode<TContext, TProps>[] = [];
        let cursor: string | undefined = path;
        while (cursor) {
            const node = nodeFor(cursor);
            result.push(node);
            cursor = node.parent ?? (cursor === ROOT_PATH ? undefined : ROOT_PATH);
        }
        return result;
    };

    const activeNodesFor = (leaves: readonly string[]): RuntimeNode<TContext, TProps>[] => {
        const paths = new Set<string>();
        leaves.forEach((leaf) => ancestry(leaf).forEach((node) => paths.add(node.path)));
        return Array.from(paths).map(nodeFor).sort((left, right) => left.order - right.order);
    };

    const resolveInitialLeaves = (path: string): string[] => {
        const node = nodeFor(path);
        if (node.children.length === 0 || node.type === 'final') return path === ROOT_PATH ? [] : [path];
        if (node.type === 'parallel') return node.children.flatMap(resolveInitialLeaves);

        if (!node.initial) throw new Error(`Machine state requires an initial child: ${path}`);
        const explicitPath = path === ROOT_PATH ? node.initial : `${path}.${node.initial}`;
        const initialPath = nodes.has(explicitPath) ? explicitPath : node.initial;
        return resolveInitialLeaves(initialPath);
    };

    const resolveTargetPath = (target: string, source: RuntimeNode<TContext, TProps>): string => {
        if (target.startsWith('#')) {
            const byId = ids.get(target.slice(1));
            if (!byId) throw new Error(`Unknown machine state id: ${target}`);
            return byId;
        }
        if (target === ROOT_PATH || nodes.has(target)) return target;
        if (source.parent) {
            const sibling = `${source.parent}.${target}`;
            if (nodes.has(sibling)) return sibling;
        }
        if (nodes.has(target)) return target;
        throw new Error(`Unknown machine state: ${target}`);
    };

    const stateValueFor = (leaves: readonly string[]): MachineStateValue => (
        leaves.length === 1 ? leaves[0] as string : Object.freeze([...leaves])
    );

    const tagsFor = (leaves: readonly string[]): readonly string[] => Object.freeze(Array.from(new Set(
        activeNodesFor(leaves).flatMap((node) => node.tags),
    )));

    const generationsFor = (): Readonly<Record<string, number>> => Object.freeze(Object.fromEntries(activeGenerations));

    let activeLeaves = resolveInitialLeaves(ROOT_PATH);
    let snapshot: MachineSnapshot<TContext, TProps> = Object.freeze({
        value: stateValueFor(activeLeaves),
        tags: tagsFor(activeLeaves),
        context: Object.freeze({...options.context}) as Readonly<TContext>,
        props: Object.freeze({...options.props ?? {}}) as Readonly<TProps>,
        generations: Object.freeze({}),
    });

    const makeScope = (
        event: MachineEventObject,
        currentSnapshot: MachineSnapshot<TContext, TProps> = snapshot,
    ): MachineScope<TContext, TProps> => ({
        snapshot: currentSnapshot,
        context: currentSnapshot.context,
        props: currentSnapshot.props,
        event,
        state: currentSnapshot.value,
    });

    const cleanupKey = (path: string): string => `${path}:${activeGenerations.get(path) ?? 0}`;

    const rememberCleanup = (path: string, cleanup: void | (() => void)): void => {
        if (typeof cleanup !== 'function') return;
        const key = cleanupKey(path);
        const stack = cleanups.get(key) ?? [];
        stack.push(cleanup);
        cleanups.set(key, stack);
    };

    const runCleanups = (path: string): void => {
        const key = cleanupKey(path);
        const stack = cleanups.get(key);
        if (!stack) return;
        stack.splice(0).reverse().forEach((cleanup) => cleanup());
        cleanups.delete(key);
    };

    const effectFor = (ref: MachineEffectRef<TContext, TProps>): MachineEffect<TContext, TProps> | undefined => (
        typeof ref === 'function' ? ref : options.effects?.[ref]
    );

    const runEffects = (
        refs: MachineEffectRef<TContext, TProps>[],
        event: MachineEventObject,
        path: string,
        remember = true,
    ): void => {
        refs.forEach((ref) => {
            const effect = effectFor(ref);
            if (!effect) throw new Error(`Unknown machine effect: ${String(ref)}`);
            const cleanup = effect(makeScope(event));
            if (remember) rememberCleanup(path, cleanup);
        });
    };

    const reducerFor = (ref: MachineReducerRef<TContext, TProps>): MachineReducer<TContext, TProps> | undefined => (
        typeof ref === 'function' ? ref : options.reducers?.[ref]
    );

    const applyReducers = (
        refs: MachineReducerRef<TContext, TProps>[],
        event: MachineEventObject,
        base: MachineSnapshot<TContext, TProps>,
    ): Pick<MachineSnapshot<TContext, TProps>, 'context' | 'props'> => {
        const contextUpdates: Partial<TContext> = {};
        const propUpdates: Partial<TProps> = {};
        const contextKeys = new Set<PropertyKey>();
        const propKeys = new Set<PropertyKey>();

        refs.forEach((ref) => {
            const reducer = reducerFor(ref);
            if (!reducer) throw new Error(`Unknown machine reducer: ${String(ref)}`);
            const update = reducer(makeScope(event, base));
            if (!update) return;

            Reflect.ownKeys(update.context ?? {}).forEach((key) => {
                if (contextKeys.has(key)) throw new Error(`Conflicting machine context update: ${String(key)}`);
                contextKeys.add(key);
                Reflect.set(contextUpdates as object, key, Reflect.get(update.context as object, key));
            });
            Reflect.ownKeys(update.props ?? {}).forEach((key) => {
                if (propKeys.has(key)) throw new Error(`Conflicting machine prop update: ${String(key)}`);
                propKeys.add(key);
                Reflect.set(propUpdates as object, key, Reflect.get(update.props as object, key));
            });
        });

        return {
            context: Object.freeze({...base.context, ...contextUpdates}) as Readonly<TContext>,
            props: Object.freeze({...base.props, ...propUpdates}) as Readonly<TProps>,
        };
    };

    const guardAllows = (
        guard: MachineTransitionDefinition<TContext, TProps>['guard'],
        event: MachineEventObject,
        base: MachineSnapshot<TContext, TProps>,
    ): boolean => {
        if (!guard) return true;
        const resolved = typeof guard === 'function' ? guard : options.guards?.[guard];
        if (!resolved) throw new Error(`Unknown machine guard: ${String(guard)}`);
        return resolved(makeScope(event, base));
    };

    const selectTransitions = (
        event: MachineEventObject,
        base: MachineSnapshot<TContext, TProps>,
    ): SelectedTransition<TContext, TProps>[] => {
        const selectedBySource = new Map<string, SelectedTransition<TContext, TProps>>();

        activeLeaves.forEach((leaf) => {
            for (const source of ancestry(leaf)) {
                const candidate = toArray(source.on[event.type])
                    .map(asDefinition<TContext, TProps>)
                    .find((definition) => guardAllows(definition.guard, event, base));
                if (candidate) selectedBySource.set(source.path, {source, definition: candidate});
            }
        });

        const selected = Array.from(selectedBySource.values());
        return selected.filter((candidate) => !selected.some((other) => (
            other !== candidate && isDescendant(candidate.source.path, other.source.path)
        ))).sort((left, right) => left.source.order - right.source.order);
    };

    const commonAncestor = (left: string, right: string): string => {
        const rightPaths = new Set(ancestry(right).map((node) => node.path));
        return ancestry(left).find((node) => rightPaths.has(node.path))?.path ?? ROOT_PATH;
    };

    const transitionBoundary = (source: string, target: string): string => {
        const lca = commonAncestor(source, target);
        if (source === target || lca === source) return source;
        const sourceAncestry = ancestry(source);
        const lcaIndex = sourceAncestry.findIndex((node) => node.path === lca);
        return sourceAncestry[Math.max(0, lcaIndex - 1)]?.path ?? source;
    };

    const nextLeavesFor = (selected: SelectedTransition<TContext, TProps>[]): string[] => {
        let next = [...activeLeaves];
        selected.forEach(({source, definition}) => {
            if (!definition.target) return;
            const targetPath = resolveTargetPath(definition.target, source);
            const boundary = transitionBoundary(source.path, targetPath);
            next = next.filter((leaf) => !isDescendant(leaf, boundary));
            next.push(...resolveInitialLeaves(targetPath));
        });
        return Array.from(new Set(next)).sort((left, right) => nodeFor(left).order - nodeFor(right).order);
    };

    const processEvent = (event: MachineEventObject): void => {
        if (
            typeof event.__state === 'string'
            && typeof event.__generation === 'number'
            && activeGenerations.get(event.__state) !== event.__generation
        ) return;

        const previous = snapshot;
        const selected = selectTransitions(event, previous);
        if (selected.length === 0) return;

        const nextLeaves = nextLeavesFor(selected);
        const previousNodes = activeNodesFor(activeLeaves);
        const nextNodes = activeNodesFor(nextLeaves);
        const previousPaths = new Set(previousNodes.map((node) => node.path));
        const nextPaths = new Set(nextNodes.map((node) => node.path));
        const reentryPaths = selected.flatMap(({source, definition}) => {
            if (!definition.target) return [];
            const target = resolveTargetPath(definition.target, source);
            return target === source.path ? previousNodes.filter((node) => isDescendant(node.path, source.path)).map((node) => node.path) : [];
        });
        const forcedReentry = new Set(reentryPaths);
        const exitNodes = previousNodes
            .filter((node) => !nextPaths.has(node.path) || forcedReentry.has(node.path))
            .sort((left, right) => right.path.split('.').length - left.path.split('.').length || right.order - left.order);
        const entryNodes = nextNodes
            .filter((node) => !previousPaths.has(node.path) || forcedReentry.has(node.path))
            .sort((left, right) => left.path.split('.').length - right.path.split('.').length || left.order - right.order);

        exitNodes.forEach((node) => {
            runCleanups(node.path);
            runEffects(node.exit, event, node.path, false);
            activeGenerations.delete(node.path);
        });

        const reducerRefs = [
            ...selected.flatMap(({definition}) => toArray(definition.reduce)),
            ...entryNodes.flatMap((node) => node.reduce),
        ];
        const reduced = applyReducers(reducerRefs, event, previous);

        entryNodes.forEach((node) => {
            const generation = (generationCounters.get(node.path) ?? 0) + 1;
            generationCounters.set(node.path, generation);
            activeGenerations.set(node.path, generation);
        });

        activeLeaves = nextLeaves;
        snapshot = Object.freeze({
            value: stateValueFor(activeLeaves),
            tags: tagsFor(activeLeaves),
            context: reduced.context,
            props: reduced.props,
            generations: generationsFor(),
        });

        selected.forEach(({source, definition}) => runEffects(
            toArray(definition.effects),
            event,
            source.path,
        ));
        entryNodes.forEach((node) => runEffects(node.entry, event, node.path));
        listeners.forEach((listener) => listener(snapshot));
        entryNodes.forEach((node) => {
            const generation = activeGenerations.get(node.path);
            if (generation === undefined) return;
            const entry: MachineEntry<TContext, TProps> = Object.freeze({
                path: node.path,
                generation,
                from: previous.value,
                to: snapshot.value,
                event,
                snapshot,
            });
            entryListeners.forEach((listener) => listener(entry));
        });
    };

    const machine: Machine<TContext, TProps> = {
        get state() {
            return snapshot.value;
        },
        get context() {
            return snapshot.context;
        },
        get props() {
            return snapshot.props;
        },
        getSnapshot() {
            return snapshot;
        },
        send(event) {
            if (stopped) return;
            queue.push(toEventObject(event));
            if (processing) return;

            processing = true;
            try {
                while (queue.length > 0 && !stopped) processEvent(queue.shift() as MachineEventObject);
            } finally {
                processing = false;
            }
        },
        subscribe(listener) {
            if (stopped) return () => {};
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        subscribeEntries(listener) {
            if (stopped) return () => {};
            entryListeners.add(listener);
            return () => entryListeners.delete(listener);
        },
        matches(state) {
            return activeLeaves.some((leaf) => leaf === state || leaf.split('.').at(-1) === state || isDescendant(leaf, state));
        },
        hasTag(tag) {
            return snapshot.tags.includes(tag);
        },
        stop() {
            if (stopped) return;
            activeNodesFor(activeLeaves)
                .sort((left, right) => right.path.split('.').length - left.path.split('.').length || right.order - left.order)
                .forEach((node) => runCleanups(node.path));
            listeners.clear();
            entryListeners.clear();
            queue.splice(0);
            activeGenerations.clear();
            stopped = true;
        },
    };

    activeNodesFor(activeLeaves).forEach((node) => {
        const generation = (generationCounters.get(node.path) ?? 0) + 1;
        generationCounters.set(node.path, generation);
        activeGenerations.set(node.path, generation);
    });
    const initialReducers = activeNodesFor(activeLeaves).flatMap((node) => node.reduce);
    const initialReduced = applyReducers(initialReducers, INIT_EVENT, snapshot);
    snapshot = Object.freeze({
        value: stateValueFor(activeLeaves),
        tags: tagsFor(activeLeaves),
        context: initialReduced.context,
        props: initialReduced.props,
        generations: generationsFor(),
    });
    activeNodesFor(activeLeaves).forEach((node) => runEffects(node.entry, INIT_EVENT, node.path));

    return machine;
};

export {createMachine};
export type {
    Machine,
    MachineConfig,
    MachineEffect,
    MachineEffectList,
    MachineEffectRef,
    MachineEntry,
    MachineEvent,
    MachineEventObject,
    MachineGuard,
    MachineOptions,
    MachineReducer,
    MachineReducerList,
    MachineReducerRef,
    MachineScope,
    MachineSnapshot,
    MachineStateConfig,
    MachineStateValue,
    MachineTransition,
    MachineTransitionDefinition,
    MachineUpdate,
};
